import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, CircularProgress, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface FullScreenImageDialogProps {
  open: boolean;
  onClose: () => void;
  originalBlobName: string;
  alt: string;
  getImageAsBase64: (blobName: string) => Promise<string>;
}

const FullScreenImageDialog: React.FC<FullScreenImageDialogProps> = ({ open, onClose, originalBlobName, alt, getImageAsBase64 }) => {
  const [imageSource, setImageSource] = useState<string>('');
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Load base64 image when dialog opens
  useEffect(() => {
    if (open && originalBlobName) {
      const loadImage = async () => {
        setIsLoadingImage(true);
        setImageLoadError(false);
        try {
          const base64 = await getImageAsBase64(originalBlobName);
          setImageSource(base64);
        } catch (error) {
          console.error('Failed to load image:', error);
          setImageLoadError(true);
        } finally {
          setIsLoadingImage(false);
        }
      };
      loadImage();
    }
  }, [open, originalBlobName, getImageAsBase64]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;

    // Calculate the display dimensions based on viewport constraints
    const maxWidth = window.innerWidth * 0.9; // 90% of viewport width
    const maxHeight = window.innerHeight * 0.8; // 80% of viewport height (leaving space for footer)

    let displayWidth = img.naturalWidth;
    let displayHeight = img.naturalHeight;

    // Scale down if image is too large
    if (displayWidth > maxWidth || displayHeight > maxHeight) {
      const scaleX = maxWidth / displayWidth;
      const scaleY = maxHeight / displayHeight;
      const scale = Math.min(scaleX, scaleY);

      displayWidth = displayWidth * scale;
      displayHeight = displayHeight * scale;
    }

    setImageDimensions({ width: displayWidth, height: displayHeight });
    setIsImageLoaded(true);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setIsImageLoaded(false);
    setImageLoadError(true);
    console.error('Failed to load original image');
  };

  const handleClose = () => {
    onClose();
    // Reset state when dialog closes
    setImageSource('');
    setIsLoadingImage(false);
    setIsImageLoaded(false);
    setImageLoadError(false);
    setImageDimensions(null);
  };

  const getDialogStyles = () => ({
    ...styles.dialogPaper,
    // Dynamic sizing based on image loading state
    ...(imageDimensions && isImageLoaded
      ? {
          // When image is loaded, size dialog to fit image
          width: `${Math.min(imageDimensions.width, window.innerWidth)}px`,
          height: `${Math.min(imageDimensions.height, window.innerHeight)}px`,
        }
      : {
          // Ensure minimum size for loader visibility
          width: '300px',
          height: '300px',
        }),
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: getDialogStyles(),
      }}
      BackdropProps={{
        sx: styles.backdrop,
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        {/* Close button overlay - only show when image is loaded */}
        {isImageLoaded && (
          <IconButton onClick={handleClose} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        )}

        {/* Loader - show when loading or when image source exists but image isn't loaded yet */}
        {(isLoadingImage || (imageSource && !isImageLoaded)) && (
          <Box sx={styles.loaderContainer}>
            <CircularProgress size={60} sx={styles.loader} />
          </Box>
        )}

        {/* Image Display */}
        {imageSource && !imageLoadError && (
          <Box sx={styles.imageContainer}>
            <img
              ref={imageRef}
              src={imageSource}
              alt={alt}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                ...styles.image,
                width: imageDimensions ? `${imageDimensions.width}px` : 'auto',
                height: imageDimensions ? `${imageDimensions.height}px` : 'auto',
                opacity: isImageLoaded ? 1 : 0,
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    maxWidth: '100vw',
    maxHeight: '100vh',
    boxShadow: 'none',
    margin: 0,
    borderRadius: 0,
    backgroundColor: 'transparent !important',
    transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
  dialogContent: {
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  closeButton: {
    position: 'absolute' as const,
    top: 16,
    right: 16,
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    position: 'relative' as const,
  },
  loaderContainer: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loader: {
    color: 'white !important',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain' as const,
    transition: 'opacity 0.3s ease-in-out',
    position: 'relative' as const,
    zIndex: 3,
  },
};

export default FullScreenImageDialog;
